/**
 * Mixpanel provider.
 *
 * Initialises the Mixpanel JS SDK and exposes a thin wrapper for
 * sending events. The project token is read from
 * REACT_APP_MIXPANEL_TOKEN at build time.
 */

import mixpanel from 'mixpanel-browser';

const TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || '';
const DEBUG = process.env.REACT_APP_ANALYTICS_DEBUG === 'true';

function logDebugError(context: string, error: unknown): void {
  if (DEBUG) {
    console.warn(`[Mixpanel] ${context}:`, error);
  }
}

let initialised = false;

/**
 * Initialise the Mixpanel SDK.
 * No-ops if the token is not configured.
 */
export function initMixpanel(): void {
  if (initialised || !TOKEN) {
    return;
  }

  try {
    mixpanel.init(TOKEN, {
      track_pageview: false, // We handle page views manually
      persistence: 'localStorage',
    });
    initialised = true;
  } catch (error) {
    console.error('Failed to initialise Mixpanel:', error);
  }
}

/**
 * Register super-properties that are attached to every subsequent event.
 */
export function setMixpanelSuperProperties(
  props: Record<string, unknown>,
): void {
  if (!initialised) return;
  try {
    mixpanel.register(props);
  } catch (error) {
    logDebugError('setMixpanelSuperProperties', error);
  }
}

/**
 * Send an event to Mixpanel.
 */
export function trackMixpanelEvent(
  eventName: string,
  props?: Record<string, unknown>,
): void {
  if (!initialised) return;

  try {
    mixpanel.track(eventName, props);
  } catch (error) {
    logDebugError('trackMixpanelEvent', error);
  }
}

/**
 * Identify a user in Mixpanel (call after login).
 */
export function identifyMixpanelUser(userId: string): void {
  if (!initialised) return;
  try {
    mixpanel.identify(userId);
  } catch (error) {
    logDebugError('identifyMixpanelUser', error);
  }
}

/**
 * Reset Mixpanel identity (call on logout).
 */
export function resetMixpanel(): void {
  if (!initialised) return;
  try {
    mixpanel.reset();
  } catch (error) {
    logDebugError('resetMixpanel', error);
  }
}
